<script setup>
  import { ref, computed } from "vue";
  import { BarChart } from "vue-chart-3"; // For Bar Chart
  import { Chart as ChartJS, registerables } from "chart.js";

  // Register Chart.js components
  ChartJS.register(...registerables);
  const props = defineProps({
    data: {
      type: Object,
      default: null,
    },
    isManager: {
      type: Boolean,
      default: "",
    },
  });

  // Define the VM data
  const vmData = props.data;

  // Format uptime from seconds to human-readable format
  const formatUptime = (uptimeInSeconds) => {
    const hours = Math.floor(uptimeInSeconds / 3600);
    const minutes = Math.floor((uptimeInSeconds % 3600) / 60);
    const seconds = uptimeInSeconds % 60;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  // Format bytes to human-readable format (e.g., MB, GB)
  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    else if (bytes < 1048576) return `${(bytes / 1024).toFixed(2)} KB`;
    else if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(2)} MB`;
    else return `${(bytes / 1073741824).toFixed(2)} GB`;
  };

  // Disk usage chart data
  const diskChartData = computed(() => {
    return {
      labels: ["Disk Read", "Disk Write"],
      datasets: [
        {
          label: "Disk Usage",
          data: [
            vmData.disk_details[0].read_bytes,
            vmData.disk_details[0].write_bytes,
          ],
          borderColor: "#4CAF50", // Green color
          backgroundColor: "#81C784", // Light green color
          borderWidth: 1,
        },
      ],
    };
  });

  // Network usage chart data
  const networkChartData = computed(() => {
    return {
      labels: ["RX", "TX"],
      datasets: [
        {
          label: "Network Traffic",
          data: [
            vmData.nic_details[0].rx_octets,
            vmData.nic_details[0].tx_octets,
          ],
          borderColor: "#2196F3", // Blue color
          backgroundColor: "#64B5F6", // Light blue color
          borderWidth: 1,
        },
      ],
    };
  });

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${formatBytes(context.raw)}`;
          },
        },
      },
    },
  };
</script>
<template>
  <div
    class="p-6 bg-gray-100 grid grid-cols-1 xl:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 sm:grid-cols-2 gap-3"
    dir="ltr"
  >
    <!-- VM Status -->
    <div class="p-5 bg-white shadow rounded-lg">
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">VM Status</h2>
      <div class="space-y-3">
        <div>
          <strong class="font-medium">State:</strong> {{ vmData.state }}
        </div>
        <div>
          <strong class="font-medium">Driver:</strong> {{ vmData.driver }}
        </div>
        <div>
          <strong class="font-medium">Hypervisor:</strong>
          {{ vmData.hypervisor }}
        </div>
        <div>
          <strong class="font-medium">Hypervisor OS:</strong>
          {{ vmData.hypervisor_os }}
        </div>
        <div>
          <strong class="font-medium">Uptime:</strong>
          {{ formatUptime(vmData.uptime) }}
        </div>
        <div>
          <strong class="font-medium">Config Drive:</strong>
          {{ vmData.config_drive ? "Enabled" : "Disabled" }}
        </div>
      </div>
    </div>

    <!-- Resource Usage -->
    <div class="p-5 bg-white shadow rounded-lg">
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">Resource Usage</h2>
      <div class="space-y-3">
        <div>
          <strong class="font-medium">Number of CPUs:</strong>
          {{ vmData.num_cpus }}
        </div>
        <div>
          <strong class="font-medium">Number of NICs:</strong>
          {{ vmData.num_nics }}
        </div>
        <div>
          <strong class="font-medium">Number of Disks:</strong>
          {{ vmData.num_disks }}
        </div>
      </div>
    </div>

    <!-- CPU Details -->
    <div class="p-5 bg-white shadow rounded-lg">
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">CPU Details</h2>
      <div
        v-if="vmData.cpu_details && vmData.cpu_details[0].utilisation !== null"
      >
        <div>
          <strong class="font-medium">CPU Utilisation:</strong>
          {{ vmData.cpu_details[0].utilisation }}%
        </div>
      </div>
      <div v-else>
        <div>
          <strong class="font-medium">CPU Utilisation:</strong> Data not
          available
        </div>
      </div>
    </div>

    <!-- Memory Details -->
    <div class="p-5 bg-white shadow rounded-lg">
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">Memory Details</h2>
      <div>
        <div>
          <strong class="font-medium">Maximum Memory:</strong>
          {{ vmData.memory_details.maximum }} MB
        </div>
        <div>
          <strong class="font-medium">Used Memory:</strong>
          {{ vmData.memory_details.used }} MB
        </div>
      </div>
    </div>

    <!-- Network Details (NIC) -->
    <div class="p-5 bg-white shadow rounded-lg">
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">
        Network Interface
      </h2>
      <div>
        <div>
          <strong class="font-medium">MAC Address:</strong>
          {{ vmData.nic_details[0].mac_address }}
        </div>
        <div>
          <strong class="font-medium">RX Octets:</strong>
          {{ vmData.nic_details[0].rx_octets }} bytes
        </div>
        <div>
          <strong class="font-medium">TX Octets:</strong>
          {{ vmData.nic_details[0].tx_octets }} bytes
        </div>
        <div>
          <strong class="font-medium">RX Packets:</strong>
          {{ vmData.nic_details[0].rx_packets }}
        </div>
        <div>
          <strong class="font-medium">TX Packets:</strong>
          {{ vmData.nic_details[0].tx_packets }}
        </div>
        <div>
          <strong class="font-medium">RX Errors:</strong>
          {{ vmData.nic_details[0].rx_errors }}
        </div>
        <div>
          <strong class="font-medium">TX Errors:</strong>
          {{ vmData.nic_details[0].tx_errors }}
        </div>
      </div>
    </div>

    <!-- Disk Details -->
    <div class="p-5 bg-white shadow rounded-lg">
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">Disk Usage</h2>
      <div>
        <div>
          <strong class="font-medium">Read Bytes:</strong>
          {{ formatBytes(vmData.disk_details[0].read_bytes) }}
        </div>
        <div>
          <strong class="font-medium">Write Bytes:</strong>
          {{ formatBytes(vmData.disk_details[0].write_bytes) }}
        </div>
        <div>
          <strong class="font-medium">Read Requests:</strong>
          {{ vmData.disk_details[0].read_requests }}
        </div>
        <div>
          <strong class="font-medium">Write Requests:</strong>
          {{ vmData.disk_details[0].write_requests }}
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div
      class="p-5 bg-white shadow rounded-lg col-span-1 xl:col-span-2 md:col-span-2 lg:col-span-2 sm:col-span-2"
    >
      <h2 class="text-2xl font-semibold text-gray-700 mb-4">
        Disk and Network Usage
      </h2>
      <!-- Disk Read/Write Chart -->
      <div
        class="grid grid-cols-1 xl:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 sm:grid-cols-2 gap-6"
      >
        <div v-if="diskChartData">
          <BarChart :chart-data="diskChartData" :chart-options="chartOptions" />
        </div>
        <!-- Network RX/TX Chart -->
        <div v-if="networkChartData">
          <BarChart
            :chart-data="networkChartData"
            :chart-options="chartOptions"
          />
        </div>
      </div>
    </div>
  </div>
</template>
